import React, { useState } from 'react';
import { PromptRuleService } from '../services/promptRuleService';
import '../styles/prompt-rules-panel.css';

// Available fields that rules can address
const AVAILABLE_FIELDS = [
  { value: '', label: '-- Select a field --' },
  { value: 'patient.nhi', label: 'Patient - NHI' },
  { value: 'patient.first_name', label: 'Patient - First Name' },
  { value: 'patient.last_name', label: 'Patient - Last Name' },
  { value: 'patient.dob', label: 'Patient - Date of Birth' },
  { value: 'patient.phone', label: 'Patient - Phone' },
  { value: 'patient.email', label: 'Patient - Email' },
  { value: 'referral.acc_number', label: 'Referral - ACC Number' },
  { value: 'referral.reason', label: 'Referral - Reason' },
  { value: 'referral.priority', label: 'Referral - Priority' },
  { value: 'referral.body_part', label: 'Referral - Body Part' },
  { value: 'referral.body_side', label: 'Referral - Body Side' },
  { value: 'referral.date_of_injury', label: 'Referral - Date of Injury' },
  { value: 'referral.mechanism_of_injury', label: 'Referral - Mechanism of Injury' },
  { value: 'referral.clinical_notes', label: 'Referral - Clinical Notes' },
  { value: 'referral.referral_type', label: 'Referral - Referral Type' },
  { value: 'referral.is_acc_related', label: 'Referral - Is ACC Related' },
  { value: 'referral.funder', label: 'Referral - Funder' },
  { value: 'referral.imaging_performed', label: 'Referral - Imaging Performed' },
  { value: 'referral.imaging_facility', label: 'Referral - Imaging Facility' },
  { value: 'provider.name', label: 'Provider - Name' },
  { value: 'provider.organization', label: 'Provider - Organization' },
  { value: 'provider.email', label: 'Provider - Email' },
  { value: 'provider.edi', label: 'Provider - EDI' },
  { value: 'provider.phone', label: 'Provider - Phone' },
  { value: 'provider.address', label: 'Provider - Address' },
];

interface PromptRule {
  id: string;
  section: string;
  field_name: string;
  rule_text: string;
  priority: number;
  description: string;
  status: 'active' | 'inactive' | 'testing';
  created_at: string;
  updated_at: string;
}

interface PromptRulesPanelProps {
  rules: PromptRule[];
  onRuleUpdated: () => void;
}

export const PromptRulesPanel: React.FC<PromptRulesPanelProps> = ({ rules, onRuleUpdated }) => {
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [selectedRule, setSelectedRule] = useState<PromptRule | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedRule, setEditedRule] = useState<Partial<PromptRule>>({});

  const formatSection = (section: string) => {
    return section
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'badge-active';
      case 'testing':
        return 'badge-testing';
      case 'inactive':
        return 'badge-inactive';
      default:
        return '';
    }
  };

  const handleStatusToggle = async (ruleId: string, currentStatus: string) => {
    try {
      setIsUpdating(ruleId);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await PromptRuleService.updateStatus(ruleId, newStatus);
      onRuleUpdated();
    } catch (error) {
      console.error('Failed to update rule status:', error);
      alert('Failed to update rule status. Please try again.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleEditClick = () => {
    if (selectedRule) {
      setEditedRule({
        section: selectedRule.section,
        field_name: selectedRule.field_name || '',
        rule_text: selectedRule.rule_text,
        priority: selectedRule.priority,
        description: selectedRule.description || '',
        status: selectedRule.status
      });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedRule) return;

    try {
      setIsUpdating(selectedRule.id);
      await PromptRuleService.updateRule(selectedRule.id, editedRule);
      setIsEditing(false);
      setSelectedRule(null);
      onRuleUpdated();
    } catch (error) {
      console.error('Failed to update rule:', error);
      alert('Failed to update rule. Please try again.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedRule({});
  };

  return (
    <div className="prompt-rules-panel">
      <table className="rules-table">
        <thead>
          <tr>
            <th className="col-section">Section</th>
            <th className="col-description">Description</th>
            <th className="col-field">Field</th>
            <th className="col-status">Status</th>
            <th className="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <tr key={rule.id} className="rules-table-row">
              <td className="col-section">
                <code className="section-code">{formatSection(rule.section)}</code>
              </td>
              <td className="col-description">
                {rule.description || <span className="text-muted">—</span>}
              </td>
              <td className="col-field">
                {rule.field_name ? <strong>{rule.field_name}</strong> : <span className="text-muted">—</span>}
              </td>
              <td className="col-status">
                <span className={`status-badge ${getStatusBadgeClass(rule.status)}`}>
                  {rule.status.charAt(0).toUpperCase() + rule.status.slice(1)}
                </span>
              </td>
              <td className="col-actions">
                <div className="action-buttons">
                  <button
                    className="btn btn-sm btn-view"
                    onClick={() => setSelectedRule(rule)}
                    title="View rule details"
                  >
                    View
                  </button>
                  <button
                    className={`btn btn-sm ${rule.status === 'active' ? 'btn-deactivate' : 'btn-activate'}`}
                    onClick={() => handleStatusToggle(rule.id, rule.status)}
                    disabled={isUpdating === rule.id}
                    title={rule.status === 'active' ? 'Deactivate rule' : 'Activate rule'}
                  >
                    {isUpdating === rule.id ? '...' : rule.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Rule Details Modal */}
      {selectedRule && (
        <>
          <div className="modal-backdrop" onClick={() => {
            if (isEditing) {
              handleCancelEdit();
            } else {
              setSelectedRule(null);
            }
          }}></div>
          <div className="rule-details-modal">
            <div className="modal-header">
              <h2>Rule Details</h2>
              <button 
                className="btn-close-modal"
                onClick={() => {
                  if (isEditing) {
                    handleCancelEdit();
                  } else {
                    setSelectedRule(null);
                  }
                }}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {isEditing ? (
                <>
                  <div className="detail-section">
                    <label>
                      <h4>Section</h4>
                      <input
                        type="text"
                        value={editedRule.section || ''}
                        onChange={(e) => setEditedRule({ ...editedRule, section: e.target.value })}
                        className="form-input"
                        required
                      />
                    </label>
                  </div>

                  <div className="detail-section">
                    <label>
                      <h4>Description</h4>
                      <input
                        type="text"
                        value={editedRule.description || ''}
                        onChange={(e) => setEditedRule({ ...editedRule, description: e.target.value })}
                        className="form-input"
                      />
                    </label>
                  </div>

                  <div className="detail-section">
                    <label>
                      <h4>Field Name <span className="required">*</span></h4>
                      <select
                        value={editedRule.field_name || ''}
                        onChange={(e) => setEditedRule({ ...editedRule, field_name: e.target.value })}
                        className="form-select"
                        required
                      >
                        {AVAILABLE_FIELDS.map(field => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="detail-section">
                    <label>
                      <h4>Rule Text</h4>
                      <textarea
                        value={editedRule.rule_text || ''}
                        onChange={(e) => setEditedRule({ ...editedRule, rule_text: e.target.value })}
                        className="form-textarea"
                        rows={10}
                        required
                      />
                    </label>
                  </div>

                  <div className="detail-section">
                    <label>
                      <h4>Priority</h4>
                      <input
                        type="number"
                        value={editedRule.priority || 100}
                        onChange={(e) => setEditedRule({ ...editedRule, priority: parseInt(e.target.value) || 100 })}
                        className="form-input"
                        min="1"
                      />
                    </label>
                  </div>

                  <div className="detail-section">
                    <label>
                      <h4>Status</h4>
                      <select
                        value={editedRule.status || 'active'}
                        onChange={(e) => setEditedRule({ ...editedRule, status: e.target.value as any })}
                        className="form-select"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="testing">Testing</option>
                      </select>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-section">
                    <h4>Section</h4>
                    <code className="section-code">{formatSection(selectedRule.section)}</code>
                  </div>

                  {selectedRule.description && (
                    <div className="detail-section">
                      <h4>Description</h4>
                      <p>{selectedRule.description}</p>
                    </div>
                  )}

                  {selectedRule.field_name && (
                    <div className="detail-section">
                      <h4>Field</h4>
                      <p><strong>{selectedRule.field_name}</strong></p>
                    </div>
                  )}

                  <div className="detail-section">
                    <h4>Rule Text</h4>
                    <div className="rule-text-box">
                      <pre>{selectedRule.rule_text}</pre>
                    </div>
                  </div>
                </>
              )}

              {!isEditing && (
                <div className="detail-section detail-meta">
                  <div className="meta-item">
                    <span className="meta-label">Status:</span>
                    <span className={`status-badge ${getStatusBadgeClass(selectedRule.status)}`}>
                      {selectedRule.status.charAt(0).toUpperCase() + selectedRule.status.slice(1)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {isEditing ? (
                <>
                  <button 
                    className="btn btn-secondary"
                    onClick={handleCancelEdit}
                    disabled={isUpdating === selectedRule.id}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleSaveEdit}
                    disabled={isUpdating === selectedRule.id || !editedRule.section || !editedRule.rule_text || !editedRule.field_name}
                  >
                    {isUpdating === selectedRule.id ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn btn-secondary"
                    onClick={handleEditClick}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setSelectedRule(null)}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
